import './logbox'; // PRIMEIRO: silencia avisos antes de carregar expo-notifications
import * as Notifications from 'expo-notifications';
import * as Device from 'expo-device';
import Constants from 'expo-constants';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Sport } from '@/types';
import { useFavoritesStore } from '@/store/favorites';

const PUSH_TOKEN_KEY = 'hp-sports-expo-push-token';

Notifications.setNotificationHandler({
  handleNotification: async () => ({
    // expo-notifications 0.32: banner + lista substituem shouldShowAlert.
    // Sem isto a notificação NÃO aparece com o app em primeiro plano.
    shouldShowBanner: true,
    shouldShowList: true,
    shouldShowAlert: true, // compat com versões anteriores
    shouldPlaySound: true,
    shouldSetBadge: true,
  }) as any,
});

export async function registerForPushNotifications(): Promise<string | null> {
  if (!Device.isDevice) {
    console.warn('Push notifications requerem dispositivo físico.');
    return null;
  }

  const { status: existing } = await Notifications.getPermissionsAsync();
  let finalStatus = existing;

  if (existing !== 'granted') {
    const { status } = await Notifications.requestPermissionsAsync();
    finalStatus = status;
  }

  if (finalStatus !== 'granted') return null;

  if (Platform.OS === 'android') {
    await Notifications.setNotificationChannelAsync('live-events', {
      name: 'Eventos Ao Vivo',
      importance: Notifications.AndroidImportance.MAX,
      vibrationPattern: [0, 250, 250, 250],
      lightColor: '#FF6B35',
    });
    await Notifications.setNotificationChannelAsync('game-start', {
      name: 'Início de Jogo',
      importance: Notifications.AndroidImportance.HIGH,
      vibrationPattern: [0, 150],
    });
  }

  // Push remoto (token) foi removido do Expo Go no SDK 53. Pular para não gerar
  // warning — as notificações LOCAIS (nossos alertas ao vivo) funcionam normalmente.
  if (Constants.executionEnvironment === 'storeClient') {
    return null;
  }

  const projectId = Constants.expoConfig?.extra?.eas?.projectId;
  if (!projectId) {
    // Push tokens requerem EAS projectId configurado (Development Build)
    return null;
  }

  try {
    const token = await Notifications.getExpoPushTokenAsync({ projectId });
    await AsyncStorage.setItem(PUSH_TOKEN_KEY, token.data);
    return token.data;
  } catch {
    return null;
  }
}

export async function getStoredPushToken(): Promise<string | null> {
  return AsyncStorage.getItem(PUSH_TOKEN_KEY);
}

export async function registerPushTokenWithServer(token: string): Promise<void> {
  const apiBase = process.env.EXPO_PUBLIC_API_BASE;
  if (!apiBase || !token) return;

  const { favorites, preferences } = useFavoritesStore.getState();
  try {
    await fetch(`${apiBase}/push/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        platform: Platform.OS,
        deviceName: Device.deviceName,
        appVersion: Constants.expoConfig?.version,
        preferences,
        favorites,
      }),
    });
  } catch (error) {
    console.warn('[Push] Falha ao registrar token no servidor:', error);
  }
}

export async function setupRemotePushNotifications(): Promise<string | null> {
  const token = await registerForPushNotifications();
  if (token) await registerPushTokenWithServer(token);
  return token;
}

export async function syncPushRegistration(): Promise<boolean> {
  const token = await getStoredPushToken();
  if (!token) return false;
  await registerPushTokenWithServer(token);
  return true;
}

export async function sendPushTest(): Promise<{ ok: boolean; message: string }> {
  const apiBase = process.env.EXPO_PUBLIC_API_BASE;
  if (!apiBase) return { ok: false, message: 'Servidor nao configurado.' };

  const token = await getStoredPushToken() ?? await registerForPushNotifications();
  if (!token) return { ok: false, message: 'Permita notificacoes e tente novamente.' };

  await registerPushTokenWithServer(token);
  const res = await fetch(`${apiBase}/push/test-device`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body?.ok) {
    return { ok: false, message: body?.error ?? 'Nao foi possivel enviar o teste.' };
  }
  return { ok: true, message: 'Teste enviado.' };
}

export function addPushTokenRegistrationListener(): { remove: () => void } {
  return Notifications.addPushTokenListener((token: { data: string }) => {
    registerPushTokenWithServer(token.data).catch(() => {});
  });
}

export async function scheduleLocalNotification(
  title: string,
  body: string,
  data?: Record<string, string>
): Promise<void> {
  await Notifications.scheduleNotificationAsync({
    content: { title, body, data, sound: 'notification.wav' },
    trigger: null,
  });
}

// Ícones por tipo de evento
export function getEventEmoji(
  sport: Sport,
  eventType: string
): string {
  const map: Record<string, string> = {
    goal: '⚽',
    own_goal: '🥅',
    penalty: '🟡',
    red_card: '🟥',
    yellow_card: '🟨',
    touchdown: '🏈',
    field_goal: '🏈',
    basket: '🏀',
    buzzer: '🚨',
    game_start: '🎮',
    game_end: '🏁',
    halftime: '⏸️',
  };
  return map[eventType] ?? '📣';
}
